import {
  getBubbleResizeHandle,
  moveBubble,
  resizeBubble,
  rotateBubbleByPointerDelta,
  type BubbleHitRegion,
} from "./BubbleGeometry";
import type { ComicBubbleObject, ComicPanel, Point } from "./internalTypes";

export interface CompletedComicTransform {
  before: ComicBubbleObject;
  after: ComicBubbleObject;
}

export const DEFAULT_COMIC_TRANSFORM_THRESHOLD_CSS_PIXELS = 4;

export class ComicInteractionController {
  private original: ComicBubbleObject | null = null;
  private preview: ComicBubbleObject | null = null;
  private panel: ComicPanel | null = null;
  private startPoint: Point | null = null;
  private activationPoint: Point | null = null;
  private startZoom = 1;
  private activated = false;
  private region: BubbleHitRegion = "body";

  public constructor(
    private readonly activationThresholdCssPixels = DEFAULT_COMIC_TRANSFORM_THRESHOLD_CSS_PIXELS,
  ) {}

  public begin(
    object: ComicBubbleObject,
    panel: ComicPanel,
    region: BubbleHitRegion,
    point: Point,
    viewportZoom = 1,
  ): void {
    this.original = { ...object, data: { ...object.data } };
    this.preview = null;
    this.panel = { ...panel };
    this.startPoint = { ...point };
    this.activationPoint = null;
    this.startZoom = Number.isFinite(viewportZoom) ? Math.max(0.05, viewportZoom) : 1;
    this.activated = false;
    this.region = region;
  }

  public update(point: Point): ComicBubbleObject | null {
    if (!this.original || !this.panel || !this.startPoint) return null;
    const travel = {
      x: point.x - this.startPoint.x,
      y: point.y - this.startPoint.y,
    };
    const documentDistance = Math.hypot(travel.x, travel.y);
    const screenDistance = documentDistance * this.startZoom;
    if (!this.activated) {
      if (screenDistance < Math.max(0, this.activationThresholdCssPixels)) return null;
      this.activated = true;
      const thresholdDocumentDistance =
        Math.max(0, this.activationThresholdCssPixels) / this.startZoom;
      const thresholdRatio =
        documentDistance > 0 ? Math.min(1, thresholdDocumentDistance / documentDistance) : 0;
      this.activationPoint = {
        x: this.startPoint.x + travel.x * thresholdRatio,
        y: this.startPoint.y + travel.y * thresholdRatio,
      };
    }
    if (!this.activationPoint) return null;
    const activeDelta = {
      x: point.x - this.activationPoint.x,
      y: point.y - this.activationPoint.y,
    };
    if (this.region === "resize") {
      const handle = getBubbleResizeHandle(this.original);
      this.preview = resizeBubble(
        this.original,
        { x: handle.x + activeDelta.x, y: handle.y + activeDelta.y },
        this.panel,
      );
    } else if (this.region === "rotate") {
      this.preview = rotateBubbleByPointerDelta(
        this.original,
        this.activationPoint,
        point,
        this.panel,
      );
    } else {
      this.preview = moveBubble(this.original, activeDelta, this.panel);
    }
    return this.getPreview();
  }

  public getPreview(): ComicBubbleObject | null {
    return this.preview ? { ...this.preview, data: { ...this.preview.data } } : null;
  }

  public finish(): CompletedComicTransform | null {
    if (!this.original || !this.preview || !this.activated) {
      this.reset();
      return null;
    }
    const before = this.original;
    const after = this.preview;
    this.reset();
    const delta =
      Math.abs(after.x - before.x) +
      Math.abs(after.y - before.y) +
      Math.abs(after.width - before.width) +
      Math.abs(after.height - before.height) +
      Math.abs(after.rotation - before.rotation);
    return delta > 0.001 ? { before, after } : null;
  }

  public cancel(): ComicBubbleObject | null {
    const original = this.original ? { ...this.original, data: { ...this.original.data } } : null;
    this.reset();
    return original;
  }

  public isActive(): boolean {
    return this.original !== null;
  }

  public isActivated(): boolean {
    return this.activated;
  }

  private reset(): void {
    this.original = null;
    this.preview = null;
    this.panel = null;
    this.startPoint = null;
    this.activationPoint = null;
    this.startZoom = 1;
    this.activated = false;
    this.region = "body";
  }
}
